import lodash from 'lodash';
import { isKotSessionCartKey } from '@utilities/kotSession';
import type {
    TabsConfig,
    BadgeCounts,
    TableCart,
    FoodItem,
    MenuData,
    SelectedVariant,
    VariantMap,
} from '../types';

/**
 * Compute badge counts for each table category.
 * Returns { categoryKey: { tableName: itemCount } }
 */
export function computeBadgeCounts(cart: Record<string, TableCart>): BadgeCounts {
    const counts: BadgeCounts = {};
    if (!cart) return counts;

    Object.keys(cart).forEach((key) => {
        const catKey = key.split('-')[0];
        if (!counts[catKey]) counts[catKey] = {};

        const items = cart[key];
        const itemCount = items
            ? Object.keys(items).filter((k) => items[k] !== null && !isKotSessionCartKey(k)).length
            : 0;
        if (itemCount) counts[catKey][key] = itemCount;
    });

    return counts;
}

/**
 * Get the sorted table category entries (descending).
 */
export function getSortedTableCategories(tabs: TabsConfig): Array<[string, number]> {
    return (Object.entries(tabs) as Array<[string, number]>).sort().reverse();
}

/**
 * Build the flat list of all table names across all categories.
 */
export function getAllTableNames(tabs: TabsConfig): string[] {
    const names: string[] = [];
    Object.entries(tabs).forEach(([tab, count]) => {
        for (let i = 1; i <= count; i++) {
            names.push(`${tab}-${i}`);
        }
    });
    return names;
}

/**
 * Get active cart items (non-null) for a given table.
 */
export function getActiveCartItems(tableCart: TableCart): Array<[string, NonNullable<TableCart[string]>]> {
    return Object.entries(tableCart).filter(
        ([key, val]) => val !== null && !isKotSessionCartKey(key)
    ) as Array<[string, NonNullable<TableCart[string]>]>;
}

/**
 * Sort menu categories: use provided order, fallback to alphabetical.
 */
export function getMenuCategories(catOrder: string[], products: MenuData): string[] {
    if (catOrder?.length) return catOrder;
    return products ? Object.keys(products).sort() : [];
}

/**
 * Get sorted items within a single menu category.
 */
export function getMenuItems(products: MenuData, category: string): Array<[string, FoodItem]> {
    if (!products?.[category]) return [];
    return lodash.sortBy(
        Object.entries(products[category]) as Array<[string, FoodItem]>,
        [(o) => o[1].order],
    );
}

/**
 * Resolve the selected variant for a given composite key,
 * falling back to the first variant of the item.
 */
export function resolveSelectedVariant(
    variantMap: VariantMap,
    compositeKey: string,
    item: FoodItem
): SelectedVariant {
    return variantMap[compositeKey] || {
        value: item.variants[0].cost,
        label: `₹${item.variants[0].cost} / ${item.variants[0].size}`,
    };
}
